import { FC, ReactNode, useMemo } from 'react';

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { Query } from '@tanstack/react-query';
import type { PersistedClient } from '@tanstack/react-query-persist-client';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import dayjs from 'dayjs';

import {
  PERSISTED_QUERY_KEYS,
  REACT_QUERY_PERSIST_BUSTER,
  REACT_QUERY_PERSIST_KEY,
  queryKeys,
} from '@/react-query/constants';
import useQueryClientSetup from '@/react-query/queryClient';

type Props = {
  children: ReactNode;
};

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

/**
 * Revives TaskWeatherData stored in localStorage.
 * `NightSection.date` is a dayjs object that serializes to an ISO string —
 * JSON.parse alone can't reconstruct it.
 */
const reviveWeatherData = (data: unknown): unknown => {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) return data.map(reviveWeatherData);

  const obj = data as Record<string, unknown>;

  // Detect NightSection shape: { date: string, hours: [], warnings: [] }
  if (typeof obj.date === 'string' && Array.isArray(obj.hours) && Array.isArray(obj.warnings)) {
    return { ...obj, date: dayjs(obj.date as string) };
  }

  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, reviveWeatherData(v)]));
};

const deserialize = (cachedString: string): PersistedClient => {
  const parsed = JSON.parse(cachedString) as {
    clientState?: { queries?: Array<{ queryKey: unknown[]; state: { data: unknown } }> };
  };

  if (parsed?.clientState?.queries) {
    parsed.clientState.queries = parsed.clientState.queries.map((q) => {
      const [head] = q.queryKey ?? [];
      if (head === queryKeys.weatherForecast) {
        return { ...q, state: { ...q.state, data: reviveWeatherData(q.state.data) } };
      }
      return q;
    });
  }

  return parsed as PersistedClient;
};

const QueryProvider: FC<Props> = ({ children }) => {
  const queryClient = useQueryClientSetup();

  const persistOptions = useMemo(
    () => ({
      persister: createSyncStoragePersister({
        storage: window.localStorage,
        key: REACT_QUERY_PERSIST_KEY,
        deserialize,
      }),
      maxAge: ONE_DAY_MS,
      buster: REACT_QUERY_PERSIST_BUSTER,
      dehydrateOptions: {
        shouldDehydrateQuery: (query: Query) => {
          const key = query.queryKey;
          const [head] = key;
          if (!Array.isArray(key) || typeof head !== 'string') return false;
          if (head === queryKeys.comments) return false;
          if (key.includes('artworkComments')) return false;
          if (head === queryKeys.user && key[1] === 'order') return false;
          return PERSISTED_QUERY_KEYS.has(head);
        },
      },
    }),
    [],
  );

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      {children}
    </PersistQueryClientProvider>
  );
};

export default QueryProvider;
